import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Contact } from '@/lib/contactsApi';

type TierKey =
  | 'csuite'
  | 'vp'
  | 'director'
  | 'manager'
  | 'senior'
  | 'ic'
  | 'unknown';

interface TierDef {
  key: TierKey;
  labelKey: string;
  // Matchers run in declaration order — first match wins, so order matters:
  // c-suite phrases first (so "CEO" doesn't get classified as a generic Manager),
  // VP before Director (some titles like "VP, Director of X" exist),
  // Director before Manager before Senior, Senior before IC.
  patterns: RegExp[];
}

const TIERS: TierDef[] = [
  {
    key: 'csuite',
    labelKey: 'accounts.org_tier_csuite',
    patterns: [
      /\b(CEO|CTO|CFO|COO|CMO|CRO|CIO|CISO|Chief\s+\w+\s+Officer|Founder|Co-?Founder|President|代表取締役|社長|取締役会長|執行役員|CXO)\b/i,
    ],
  },
  {
    key: 'vp',
    labelKey: 'accounts.org_tier_vp',
    patterns: [/\bVP\b|Vice\s+President|EVP|SVP|副社長|本部長/i],
  },
  {
    key: 'director',
    labelKey: 'accounts.org_tier_director',
    patterns: [/Director|Head\s+of|部長|事業部長/i],
  },
  {
    key: 'manager',
    labelKey: 'accounts.org_tier_manager',
    patterns: [/Manager|Lead|Principal|課長|マネージャー|リーダー/i],
  },
  {
    key: 'senior',
    labelKey: 'accounts.org_tier_senior',
    patterns: [/Senior|Sr\.?|Staff|主任|シニア/i],
  },
];

function classify(title: string | null | undefined): TierKey {
  if (!title) return 'unknown';
  for (const t of TIERS) {
    for (const p of t.patterns) {
      if (p.test(title)) return t.key;
    }
  }
  return 'ic';
}

const TIER_ORDER: TierKey[] = [
  'csuite',
  'vp',
  'director',
  'manager',
  'senior',
  'ic',
  'unknown',
];

const TIER_LABELS: Record<TierKey, string> = {
  csuite: 'accounts.org_tier_csuite',
  vp: 'accounts.org_tier_vp',
  director: 'accounts.org_tier_director',
  manager: 'accounts.org_tier_manager',
  senior: 'accounts.org_tier_senior',
  ic: 'accounts.org_tier_ic',
  unknown: 'accounts.org_tier_unknown',
};

const TIER_INDENT: Record<TierKey, string> = {
  csuite: 'pl-0',
  vp: 'pl-4',
  director: 'pl-8',
  manager: 'pl-12',
  senior: 'pl-16',
  ic: 'pl-20',
  unknown: 'pl-20',
};

export function OrgChart({ contacts }: { contacts: Contact[] }) {
  const { t } = useTranslation();

  const grouped = useMemo(() => {
    const m = new Map<TierKey, Contact[]>();
    for (const c of contacts) {
      const k = classify(c.title);
      const list = m.get(k) ?? [];
      list.push(c);
      m.set(k, list);
    }
    return m;
  }, [contacts]);

  if (contacts.length === 0) {
    return (
      <p className="text-xs text-ink-500">{t('accounts.org_empty')}</p>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-[11px] text-ink-500 mb-3 leading-relaxed">
        {t('accounts.org_inferred_hint')}
      </p>
      {TIER_ORDER.map((tier) => {
        const list = grouped.get(tier) ?? [];
        if (list.length === 0) return null;
        return (
          <div key={tier} className={TIER_INDENT[tier]}>
            <div className="text-[10px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-1">
              {t(TIER_LABELS[tier])}
            </div>
            <ul className="space-y-1 mb-3">
              {list.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center gap-2 text-xs border-l-2 border-ink-200 pl-2 py-1"
                >
                  <span className="font-medium text-ink-900">{c.displayName}</span>
                  {c.title && (
                    <span className="text-ink-500 truncate">{c.title}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
